import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchPaymentSettings, savePaymentSettings, uploadPaymentQr,
  updateRestaurantSettings, refreshMe,
  fetchCostTargets, saveCostTargets,
  fetchBranding, uploadLogo, uploadCover, clearLogo, clearCover, resolveImageUrl,
  hasFeature, getCurrency,
} from "../services/api";
import { useT } from "../i18n/useT";

// One manager-facing home for the settings a hotel controls itself. Identity,
// tax numbers, branding, currency and languages stay ODRA-admin only (noted at
// the foot). Complex editors (staff, rooms, menu…) are linked, not duplicated.
const S = {
  en: {
    title: "Hotel settings", subtitle: "Settings your hotel controls. Contact ODRA for the admin-only ones at the bottom.",
    saved: "Saved.", save: "Save", saving: "Saving…", on: "On", off: "Off",
    pay_title: "Payments, service charge & VAT", pay_help: "The numbers guests pay to and the tax/service rates on the bill.",
    telebirr: "Telebirr number", cbe: "CBE Birr number", qr: "Payment QR", upload_qr: "Upload QR", qr_set: "QR uploaded",
    svc: "Service charge %", vat: "VAT %", incl: "Prices already include VAT",
    pol_title: "Policies", pol_help: "Optional behaviours for your hotel.",
    group_charge: "Charge a tab to a group / transfer bills", group_charge_h: "Let cashiers settle an F&B tab against a group block and move items between tables.",
    partner_pay: "Track partner-hotel payables", partner_pay_h: "Show and track what you owe partner hotels for outsourced rooms.",
    fiscal: "E-invoicing (dry-run)", fiscal_h: "Off, or Dry-run to preview e-invoice registration without sending.", f_off: "Off", f_mock: "Dry-run",
    retention: "Guest data retention (months)", retention_h: "Delete checked-out guest personal data after this many months. Blank / 0 = keep.",
    cost_title: "Cost targets", cost_help: "Target food & drink cost as a % of sales (used in cost reports).",
    food: "Food cost target %", drink: "Drink cost target %",
    links_title: "More settings", links_help: "These open their own pages.",
    l_partner: "Partner hotels", l_privacy: "Data protection", l_staff: "Staff", l_rooms: "Room types", l_rates: "Rate plans",
    l_tables: "Tables", l_menu: "Menu", l_services: "Services catalog", l_messaging: "Guest messaging (Telegram)",
    admin_title: "Managed by ODRA (admin only)",
    admin_h: "Hotel legal name, TIN, VAT reg. no., business address & phone, invoice prefix, tax mode, currency, languages, Google review link. Ask ODRA support to change these.",
    err: "Could not save.",
    brand_title: "Branding & region", brand_help: "Your logo, cover image and country.",
    country: "Country", c_et: "Ethiopia (EN + አማርኛ + Oromiffaa)", c_nl: "Netherlands (EN + Nederlands)", c_other: "Other / English only",
    country_h: "Sets the default guest language options (unless ODRA has set a custom language list).",
    logo: "Logo", cover: "Cover image", upload: "Upload", replace: "Replace", remove: "Remove",
    brand_locked: "Custom logo & cover are available on the Hotel and Enterprise plans.",
  },
  am: {
    title: "የሆቴል ቅንብሮች", subtitle: "ሆቴልዎ የሚቆጣጠራቸው ቅንብሮች። ከታች ያሉት በአስተዳዳሪ ብቻ ለሚስተካከሉ ODRAን ያግኙ።",
    saved: "ተቀምጧል።", save: "አስቀምጥ", saving: "በማስቀመጥ ላይ…", on: "በርቷል", off: "ጠፍቷል",
    pay_title: "ክፍያ፣ የአገልግሎት ክፍያና ተ.እ.ታ", pay_help: "እንግዶች የሚከፍሉባቸው ቁጥሮችና በሂሳቡ ላይ ያሉ ተመኖች።",
    telebirr: "የቴሌብር ቁጥር", cbe: "የሲቢኢ ብር ቁጥር", qr: "የክፍያ QR", upload_qr: "QR ስቀል", qr_set: "QR ተስቅሏል",
    svc: "የአገልግሎት ክፍያ %", vat: "ተ.እ.ታ %", incl: "ዋጋዎች ተ.እ.ታን ያካትታሉ",
    pol_title: "ፖሊሲዎች", pol_help: "ለሆቴልዎ አማራጭ ባህሪያት።",
    group_charge: "ወደ ቡድን አስከፍል / ሂሳብ አዛውር", group_charge_h: "ገንዘብ ተቀባዮች የF&B ሂሳብን ወደ ቡድን እንዲያስከፍሉና በጠረጴዛዎች መካከል እንዲያዛውሩ ይፍቀዱ።",
    partner_pay: "የአጋር ሆቴል ክፍያዎችን ተከታተል", partner_pay_h: "ለውጭ ክፍሎች ለአጋር ሆቴሎች የሚከፈለውን አሳይና ተከታተል።",
    fiscal: "ኢ-ኢንቮይስ (ሙከራ)", fiscal_h: "ጠፍቷል ወይም ሳይላክ ለመመልከት ሙከራ።", f_off: "ጠፍቷል", f_mock: "ሙከራ",
    retention: "የእንግዳ መረጃ ማቆያ (ወራት)", retention_h: "ከወጡ በኋላ የእንግዳ የግል መረጃ ከዚህ ወራት በኋላ ይሰረዝ። ባዶ / 0 = ይቆይ።",
    cost_title: "የወጪ ኢላማዎች", cost_help: "የምግብና መጠጥ ወጪ ከሽያጭ በ% (በወጪ ሪፖርቶች ይጠቀማል)።",
    food: "የምግብ ወጪ ኢላማ %", drink: "የመጠጥ ወጪ ኢላማ %",
    links_title: "ተጨማሪ ቅንብሮች", links_help: "እነዚህ የራሳቸውን ገጽ ይከፍታሉ።",
    l_partner: "አጋር ሆቴሎች", l_privacy: "የመረጃ ጥበቃ", l_staff: "ሰራተኞች", l_rooms: "የክፍል አይነቶች", l_rates: "የዋጋ እቅዶች",
    l_tables: "ጠረጴዛዎች", l_menu: "ምናሌ", l_services: "የአገልግሎት ካታሎግ", l_messaging: "የእንግዳ መልእክት (ቴሌግራም)",
    admin_title: "በODRA የሚተዳደር (አስተዳዳሪ ብቻ)",
    admin_h: "የሆቴል ህጋዊ ስም፣ ቲን፣ የተ.እ.ታ ቁጥር፣ አድራሻና ስልክ፣ የደረሰኝ ቅድመ-ቅጥያ፣ የታክስ ዘዴ፣ ገንዘብ፣ ቋንቋዎች፣ የGoogle ግምገማ ማገናኛ። ለማስተካከል ODRA ድጋፍን ይጠይቁ።",
    err: "ማስቀመጥ አልተቻለም።",
    brand_title: "አርማ እና አካባቢ", brand_help: "አርማዎ፣ የሽፋን ምስልዎና አገርዎ።",
    country: "አገር", c_et: "ኢትዮጵያ (EN + አማርኛ + Oromiffaa)", c_nl: "ኔዘርላንድ (EN + Nederlands)", c_other: "ሌላ / እንግሊዝኛ ብቻ",
    country_h: "ነባሪ የእንግዳ ቋንቋ አማራጮችን ያዘጋጃል (ODRA ብጁ ዝርዝር ካላዘጋጀ በስተቀር)።",
    logo: "አርማ", cover: "የሽፋን ምስል", upload: "ስቀል", replace: "ተካ", remove: "አስወግድ",
    brand_locked: "ብጁ አርማና ሽፋን በሆቴል እና ኢንተርፕራይዝ ዕቅዶች ይገኛሉ።",
  },
  om: {
    title: "Qindaa'ina hoteelaa", subtitle: "Qindaa'ina hoteelli kee to'atu. Kanneen gadii admin qofaaf ODRA quunnami.",
    saved: "Olkaa'ame.", save: "Olkaa'i", saving: "Olkaa'aa jira…", on: "Banaa", off: "Cufaa",
    pay_title: "Kaffaltii, gatii tajaajilaa & VAT", pay_help: "Lakkoofsota keessummoonni kaffalanii fi reetii herrega irraa.",
    telebirr: "Lakkoofsa Telebirr", cbe: "Lakkoofsa CBE Birr", qr: "QR kaffaltii", upload_qr: "QR ol kaa'i", qr_set: "QR ol kaa'ame",
    svc: "Gatii tajaajilaa %", vat: "VAT %", incl: "Gatiiwwan VAT of keessaa qabu",
    pol_title: "Imaammatoota", pol_help: "Amaloota filannoo hoteelaa keetiif.",
    group_charge: "Gareetti kaffalchiisi / herrega dabarsi", group_charge_h: "Qeesharoonni herrega F&B gareetti akka kaffalchiisanii fi minjaalota gidduu akka dabarsan hayyami.",
    partner_pay: "Kaffaltii hoteela hidhataa hordofi", partner_pay_h: "Kutaa alaatiif hoteela hidhataaf kan kaffalamu agarsiisi.",
    fiscal: "E-invoice (yaalii)", fiscal_h: "Cufaa, yookiin osoo hin ergiin yaaliin ilaali.", f_off: "Cufaa", f_mock: "Yaalii",
    retention: "Turtii daataa keessummaa (ji'oota)", retention_h: "Erga ba'anii daataan dhuunfaa ji'oota kana booda haqamu. Duwwaa / 0 = tursi.",
    cost_title: "Galma baasii", cost_help: "Galma baasii nyaataa & dhugaatii %'n gurgurtaa.",
    food: "Galma baasii nyaataa %", drink: "Galma baasii dhugaatii %",
    links_title: "Qindaa'ina dabalataa", links_help: "Kunneen fuula ofii isaanii banu.",
    l_partner: "Hoteelota hidhataa", l_privacy: "Eegumsa daataa", l_staff: "Hojjettoota", l_rooms: "Gosoota kutaa", l_rates: "Karoora gatii",
    l_tables: "Minjaalota", l_menu: "Menu", l_services: "Kataloogii tajaajilaa", l_messaging: "Ergaa keessummaa (Telegram)",
    admin_title: "ODRAn kan bulfamu (admin qofa)",
    admin_h: "Maqaa seeraa, TIN, lakk. VAT, teessoo & bilbila, duraa-invoice, mala gibiraa, maallaqa, afaanota, liinkii Google review. Jijjiiruuf ODRA quunnami.",
    err: "Olkaa'uu hin dandeenye.",
    brand_title: "Mallattoo & naannoo", brand_help: "Logo kee, suuraa cover fi biyya.",
    country: "Biyya", c_et: "Itoophiyaa (EN + አማርኛ + Oromiffaa)", c_nl: "Netherlands (EN + Nederlands)", c_other: "Kan biraa / Ingiliffa qofa",
    country_h: "Filannoo afaan keessummaa durtii kaa'a (yoo ODRA tarree afaanii addaa hin kaa'in).",
    logo: "Logo", cover: "Suuraa cover", upload: "Ol kaa'i", replace: "Bakka buusi", remove: "Haqi",
    brand_locked: "Logo & cover addaa karoora Hotel fi Enterprise irratti argamu.",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)", padding: 16, marginBottom: 16 };
const inp = { padding: "8px 10px", border: "1px solid #d8dee2", borderRadius: 8, fontSize: 14 };

export default function HotelSettingsPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const hasServices = hasFeature("services");
  const hasCost = hasFeature("cost_control");
  const hasFront = hasFeature("front_desk");
  const canBrand = hasFeature("custom_branding");

  const [pay, setPay] = useState({ telebirr_number: "", cbe_birr_number: "", payment_qr_url: "", service_charge_pct: "", vat_pct: "", prices_include_vat: false });
  const [pol, setPol] = useState({ allow_group_charge: true, track_partner_payables: true, fiscal_provider: "none", data_retention_months: "" });
  const [cost, setCost] = useState({ food_cost_target_pct: "", drink_cost_target_pct: "" });
  const [country, setCountry] = useState("ET");
  const [branding, setBranding] = useState(null);   // { logo_url, cover_image_url }
  const [flash, setFlash] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchPaymentSettings().then((d) => setPay({
      telebirr_number: d.telebirr_number || "", cbe_birr_number: d.cbe_birr_number || "",
      payment_qr_url: d.payment_qr_url || "", service_charge_pct: d.service_charge_pct ?? "",
      vat_pct: d.vat_pct ?? "", prices_include_vat: !!d.prices_include_vat,
    })).catch(() => {});
    refreshMe().then((me) => {
      setPol({
        allow_group_charge: me.allow_group_charge !== false,
        track_partner_payables: me.track_partner_payables !== false,
        fiscal_provider: me.fiscal_provider === "mock" ? "mock" : "none",
        data_retention_months: me.data_retention_months ?? "",
      });
      setCountry(me.country || "ET");
    }).catch(() => {});
    fetchBranding().then(setBranding).catch(() => {});
    if (hasCost) fetchCostTargets().then((d) => setCost({
      food_cost_target_pct: d.food_cost_target_pct ?? "", drink_cost_target_pct: d.drink_cost_target_pct ?? "",
    })).catch(() => {});
  }, [hasCost]);

  const ok = (m) => { setFlash(m || t("saved")); setErr(""); setTimeout(() => setFlash(""), 2500); };
  const fail = () => setErr(t("err"));

  const savePay = async () => {
    try {
      await savePaymentSettings({
        telebirr_number: pay.telebirr_number || null, cbe_birr_number: pay.cbe_birr_number || null,
        payment_qr_url: pay.payment_qr_url || null,
        service_charge_pct: pay.service_charge_pct === "" ? null : Number(pay.service_charge_pct),
        vat_pct: pay.vat_pct === "" ? null : Number(pay.vat_pct),
        prices_include_vat: pay.prices_include_vat,
      });
      ok();
    } catch { fail(); }
  };
  const onQr = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    try { const r = await uploadPaymentQr(f); setPay((p) => ({ ...p, payment_qr_url: r.payment_qr_url || p.payment_qr_url })); ok(); }
    catch { fail(); }
  };
  const setPolicy = async (patch, optimistic) => {
    setPol((p) => ({ ...p, ...optimistic }));
    try { await updateRestaurantSettings(patch); ok(); } catch { fail(); setPol((p) => ({ ...p })); }
  };
  const saveCost = async () => {
    try {
      await saveCostTargets({
        food_cost_target_pct: cost.food_cost_target_pct === "" ? null : Number(cost.food_cost_target_pct),
        drink_cost_target_pct: cost.drink_cost_target_pct === "" ? null : Number(cost.drink_cost_target_pct),
      });
      ok();
    } catch { fail(); }
  };

  const onCountry = async (code) => {
    setCountry(code);
    try { await updateRestaurantSettings({ country: code }); ok(); } catch { fail(); }
  };
  const onLogo = async (e) => {
    const f = e.target.files && e.target.files[0]; e.target.value = "";
    if (!f) return;
    try { setBranding(await uploadLogo(f)); ok(); } catch { fail(); }
  };
  const onCover = async (e) => {
    const f = e.target.files && e.target.files[0]; e.target.value = "";
    if (!f) return;
    try { setBranding(await uploadCover(f)); ok(); } catch { fail(); }
  };
  const rmLogo = async () => { try { setBranding(await clearLogo()); ok(); } catch { fail(); } };
  const rmCover = async () => { try { setBranding(await clearCover()); ok(); } catch { fail(); } };

  const Toggle = ({ value, onToggle }) => (
    <button type="button" className="btn" onClick={onToggle}
      style={{ background: value ? "#0F6E56" : "#eef2f2", color: value ? "#fff" : "#5b6472", minWidth: 60 }}>
      {value ? t("on") : t("off")}
    </button>
  );
  const Row = ({ label, help, control }) => (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", padding: "8px 0", borderTop: "1px solid #f2f5f5" }}>
      <div style={{ flex: "1 1 300px" }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        {help && <div className="muted" style={{ fontSize: 12.5 }}>{help}</div>}
      </div>
      <div>{control}</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <h1 style={{ margin: "6px 0 2px" }}>⚙️ {t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>
      {flash && <div style={{ marginTop: 10, background: "#E7F3EE", color: "#0F6E56", borderRadius: 8, padding: "6px 12px", fontWeight: 600, display: "inline-block" }}>✓ {flash}</div>}
      {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}

      {/* Payments / tax */}
      <div style={{ ...CARD, marginTop: 14 }}>
        <div style={{ fontWeight: 700 }}>{t("pay_title")}</div>
        <div className="muted" style={{ fontSize: 12.5, marginBottom: 4 }}>{t("pay_help")}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginTop: 8 }}>
          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("telebirr")}<br /><input style={{ ...inp, width: "100%" }} value={pay.telebirr_number} onChange={(e) => setPay({ ...pay, telebirr_number: e.target.value })} /></label>
          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("cbe")}<br /><input style={{ ...inp, width: "100%" }} value={pay.cbe_birr_number} onChange={(e) => setPay({ ...pay, cbe_birr_number: e.target.value })} /></label>
          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("svc")}<br /><input type="number" min="0" max="100" style={{ ...inp, width: "100%" }} value={pay.service_charge_pct} onChange={(e) => setPay({ ...pay, service_charge_pct: e.target.value })} /></label>
          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("vat")}<br /><input type="number" min="0" max="100" style={{ ...inp, width: "100%" }} value={pay.vat_pct} onChange={(e) => setPay({ ...pay, vat_pct: e.target.value })} /></label>
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, fontSize: 13.5 }}>
          <input type="checkbox" checked={pay.prices_include_vat} onChange={(e) => setPay({ ...pay, prices_include_vat: e.target.checked })} />
          {t("incl")}
        </label>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <button className="btn primary" onClick={savePay}>{t("save")}</button>
          <label className="btn secondary" style={{ cursor: "pointer" }}>
            🖼 {t("upload_qr")}<input type="file" accept="image/*" style={{ display: "none" }} onChange={onQr} />
          </label>
          {pay.payment_qr_url && <span className="muted" style={{ fontSize: 12 }}>✓ {t("qr_set")}</span>}
        </div>
      </div>

      {/* Branding & region */}
      <div style={CARD}>
        <div style={{ fontWeight: 700 }}>{t("brand_title")}</div>
        <div className="muted" style={{ fontSize: 12.5 }}>{t("brand_help")}</div>
        <Row label={t("country")} help={t("country_h")}
          control={<select style={inp} value={country} onChange={(e) => onCountry(e.target.value)}>
            <option value="ET">{t("c_et")}</option>
            <option value="NL">{t("c_nl")}</option>
            <option value="OTHER">{t("c_other")}</option>
          </select>} />
        {canBrand ? (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 6 }}>
            {[["logo", branding?.logo_url, onLogo, rmLogo, "1 / 1"], ["cover", branding?.cover_image_url, onCover, rmCover, "16 / 6"]].map(([key, url, onUp, onRm, aspect]) => (
              <div key={key} style={{ flex: "1 1 240px" }}>
                <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 4 }}>{t(key)}</div>
                <div style={{ background: "#f6f3ec", border: "1px dashed #d3c8af", borderRadius: 10, minHeight: 90, display: "flex", alignItems: "center", justifyContent: "center", aspectRatio: aspect, overflow: "hidden" }}>
                  {url ? <img src={resolveImageUrl(url)} alt={t(key)} style={{ maxWidth: "100%", maxHeight: 140, objectFit: "contain" }} /> : <span className="muted" style={{ fontSize: 12 }}>—</span>}
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                  <label className="btn secondary small" style={{ cursor: "pointer" }}>
                    {url ? t("replace") : t("upload")}<input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={onUp} />
                  </label>
                  {url && <button className="btn danger small" onClick={onRm}>{t("remove")}</button>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>{t("brand_locked")}</div>
        )}
      </div>

      {/* Policies */}
      <div style={CARD}>
        <div style={{ fontWeight: 700 }}>{t("pol_title")}</div>
        <div className="muted" style={{ fontSize: 12.5 }}>{t("pol_help")}</div>
        {hasFront && <Row label={t("group_charge")} help={t("group_charge_h")}
          control={<Toggle value={pol.allow_group_charge} onToggle={() => setPolicy({ allow_group_charge: !pol.allow_group_charge }, { allow_group_charge: !pol.allow_group_charge })} />} />}
        {hasFront && <Row label={t("partner_pay")} help={t("partner_pay_h")}
          control={<Toggle value={pol.track_partner_payables} onToggle={() => setPolicy({ track_partner_payables: !pol.track_partner_payables }, { track_partner_payables: !pol.track_partner_payables })} />} />}
        <Row label={t("fiscal")} help={t("fiscal_h")}
          control={<select style={inp} value={pol.fiscal_provider} onChange={(e) => setPolicy({ fiscal_provider: e.target.value }, { fiscal_provider: e.target.value })}>
            <option value="none">{t("f_off")}</option>
            <option value="mock">{t("f_mock")}</option>
          </select>} />
        <Row label={t("retention")} help={t("retention_h")}
          control={<span style={{ display: "flex", gap: 6 }}>
            <input type="number" min="0" style={{ ...inp, width: 90 }} value={pol.data_retention_months}
              onChange={(e) => setPol({ ...pol, data_retention_months: e.target.value })} />
            <button className="btn secondary small" onClick={() => setPolicy({ data_retention_months: pol.data_retention_months === "" ? null : Number(pol.data_retention_months) }, {})}>{t("save")}</button>
          </span>} />
      </div>

      {/* Cost targets */}
      {hasCost && (
        <div style={CARD}>
          <div style={{ fontWeight: 700 }}>{t("cost_title")}</div>
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>{t("cost_help")}</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("food")}<br /><input type="number" min="0" max="100" style={{ ...inp, width: 120 }} value={cost.food_cost_target_pct} onChange={(e) => setCost({ ...cost, food_cost_target_pct: e.target.value })} /></label>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("drink")}<br /><input type="number" min="0" max="100" style={{ ...inp, width: 120 }} value={cost.drink_cost_target_pct} onChange={(e) => setCost({ ...cost, drink_cost_target_pct: e.target.value })} /></label>
            <button className="btn primary" onClick={saveCost}>{t("save")}</button>
          </div>
        </div>
      )}

      {/* Quick links to the dedicated config pages */}
      <div style={CARD}>
        <div style={{ fontWeight: 700 }}>{t("links_title")}</div>
        <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>{t("links_help")}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {hasFront && <Link className="btn secondary small" to="/front-desk/partner-hotels">{t("l_partner")}</Link>}
          {hasFront && <Link className="btn secondary small" to="/front-desk/room-types">{t("l_rooms")}</Link>}
          {hasFront && <Link className="btn secondary small" to="/front-desk/rate-plans">{t("l_rates")}</Link>}
          {hasFront && <Link className="btn secondary small" to="/front-desk/reservations">{t("l_messaging")}</Link>}
          <Link className="btn secondary small" to="/tables">{t("l_tables")}</Link>
          <Link className="btn secondary small" to="/menu">{t("l_menu")}</Link>
          {hasServices && <Link className="btn secondary small" to="/services">{t("l_services")}</Link>}
          {hasFeature("staff_mgmt") && <Link className="btn secondary small" to="/staff">{t("l_staff")}</Link>}
          <Link className="btn secondary small" to="/privacy">{t("l_privacy")}</Link>
        </div>
      </div>

      {/* Admin-only note */}
      <div style={{ ...CARD, background: "#FBF7EC", border: "1px solid #ECD9A6" }}>
        <div style={{ fontWeight: 700, color: "#8A5A0B" }}>{t("admin_title")}</div>
        <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>{t("admin_h")}</div>
      </div>
    </div>
  );
}
